/**
 * Operator log-trace calls (read-only). Shapes follow
 * backend/src/app/administration/trace_inspection/service.py. user_id values
 * are already masked by the backend when the session lacks users.pii.read.
 */
import { operatorGet, queryString, type OperatorFetch } from "../operatorHttp";

export type TraceDegraded = "none" | "unavailable";

export interface TraceSummary {
  correlation_id: string;
  time: string;
  service: string;
  entry_event: string;
  last_outcome: string | null;
  duration_ms: number;
  error_count: number;
  lines: number;
  job_id: string | null;
  session_id: string | null;
}

export interface TracePage {
  degraded: TraceDegraded;
  window: { since: string; until: string };
  items: TraceSummary[];
  next_cursor: string | null;
}

export interface TraceRecord {
  time: string;
  service: string;
  level: string;
  event: string;
  operation: string | null;
  outcome: string | null;
  code: string | null;
  duration_ms: number | null;
  provider: string | null;
  model: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  http_method: string | null;
  http_route: string | null;
  http_status: number | null;
  correlation_id: string | null;
  job_id: string | null;
  session_id: string | null;
  turn_id: string | null;
  user_id: string | null;
  message: string | null;
  extra: Record<string, unknown>;
}

export interface TraceDetailData {
  degraded: TraceDegraded;
  correlation_id: string;
  records: TraceRecord[];
  related: {
    session_id: string | null;
    previous: string | null;
    next: string | null;
  };
}

export interface TraceListQuery {
  userId?: string;
  sessionId?: string;
  since?: string;
  until?: string;
  cursor?: string | null;
}

export interface TraceApi {
  list: (query: TraceListQuery) => Promise<TracePage>;
  detail: (correlationId: string) => Promise<TraceDetailData>;
}

export const TRACE_PAGE_SIZE = 50;

export function createTraceApi(fetcher: OperatorFetch = fetch): TraceApi {
  return {
    list: (query) =>
      operatorGet(
        fetcher,
        `/traces${queryString({
          user_id: query.userId?.trim(),
          session_id: query.sessionId?.trim(),
          since: query.since,
          until: query.until,
          cursor: query.cursor,
          limit: TRACE_PAGE_SIZE,
        })}`,
      ),
    detail: (correlationId) =>
      operatorGet(fetcher, `/traces/${encodeURIComponent(correlationId)}`),
  };
}
